/**
 * wallet-core/storage.ts
 *
 * Wallet state persistence:
 * - Native (Android/iOS): encrypted via capacitor-secure-storage-plugin (Android Keystore / Keychain).
 * - Web dev: idb-keyval strings via secure-storage adapter.
 * - Fallback: localStorage if secure layer fails.
 *
 * Schema:
 *   Key       : 'wallet-state'
 *   Value     : JSON string of WalletState
 */

import { del, get } from 'idb-keyval'
import type { WalletState } from './types.js'
import { legacyWalletIdbStore, secureGet, secureRemove, secureSet } from './secure-storage.js'

const STATE_KEY = 'wallet-state'

const EMPTY_STATE: WalletState = {
  accounts: [],
  activeAccountName: null,
}

export async function loadWalletState(): Promise<WalletState> {
  try {
    const raw = await secureGet(STATE_KEY)
    if (raw) {
      return JSON.parse(raw) as WalletState
    }
  } catch (e) {
    console.warn('[wallet-core/storage] secureGet failed', e)
  }

  try {
    const legacy = await get<WalletState>(STATE_KEY, legacyWalletIdbStore)
    if (legacy && typeof legacy === 'object' && Array.isArray(legacy.accounts)) {
      await saveWalletState(legacy)
      return legacy
    }
  } catch (e) {
    console.warn('[wallet-core/storage] legacy idb read failed', e)
  }

  try {
    const raw = localStorage.getItem(STATE_KEY)
    return raw ? (JSON.parse(raw) as WalletState) : EMPTY_STATE
  } catch {
    return EMPTY_STATE
  }
}

export async function saveWalletState(state: WalletState): Promise<void> {
  const raw = JSON.stringify(state)
  try {
    await secureSet(STATE_KEY, raw)
  } catch (e) {
    console.warn('[wallet-core/storage] secureSet failed', e)
  }
  // Belt-and-suspenders for web: always keep a plain localStorage copy
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STATE_KEY, raw)
      localStorage.setItem(`pw_ss_${STATE_KEY}`, raw)
    }
  } catch (e2) {
    console.error('[wallet-core/storage] localStorage write failed', e2)
  }
}

export async function clearWalletState(): Promise<void> {
  try {
    await secureRemove(STATE_KEY)
  } catch (e) {
    console.warn('[wallet-core/storage] secureRemove failed', e)
  }
  try {
    await del(STATE_KEY, legacyWalletIdbStore)
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(STATE_KEY)
  } catch {
    // ignore
  }
}

export async function hasWallet(): Promise<boolean> {
  const state = await loadWalletState()
  return state.accounts.length > 0
}
